import { z } from 'zod';

// Schema para login
export const loginSchema = z.object({
  login: z
    .string()
    .min(1, 'Login é obrigatório')
    .min(2, 'Login deve ter pelo menos 2 caracteres')
    .max(100, 'Login deve ter no máximo 100 caracteres')
    .regex(/^[a-zA-Z0-9@._\\/-]+$/, 'Login contém caracteres inválidos'),
  senha: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(1, 'Senha deve ter pelo menos 1 caractere')
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Schema para filtros de chamados
export const chamadoFiltersSchema = z.object({
  status: z.string().optional(),
  prioridade: z.number().min(1).max(4).optional(),
  responsavel: z.string().optional(),
  dataInicio: z.string().optional(),
  dataFim: z.string().optional(),
  busca: z.string().optional()
});

export type ChamadoFiltersData = z.infer<typeof chamadoFiltersSchema>;

// Schema para comentários/interações
export const comentarioSchema = z.object({
  comentario: z
    .string()
    .min(1, 'Comentário é obrigatório')
    .min(10, 'Comentário deve ter pelo menos 10 caracteres')
    .max(1000, 'Comentário deve ter no máximo 1000 caracteres'),
  interacaoInterna: z.boolean().default(false)
});

export type ComentarioFormData = z.infer<typeof comentarioSchema>;

// Schema para configurações de usuário
export const userConfigSchema = z.object({
  nome: z
    .string()
    .min(1, 'Nome é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),
  email: z
    .string()
    .min(1, 'Email é obrigatório')
    .email('Email deve ter um formato válido'),
  notificacoes: z.object({
    emailNovoChamado: z.boolean().default(true),
    emailStatusChange: z.boolean().default(true),
    emailSlaAlerta: z.boolean().default(true)
  }).default({
    emailNovoChamado: true,
    emailStatusChange: true,
    emailSlaAlerta: true
  })
});

export type UserConfigData = z.infer<typeof userConfigSchema>;

// Schema para criação/edição de chamado (caso implementado no futuro)
export const chamadoSchema = z.object({
  assunto: z
    .string()
    .min(1, 'Assunto é obrigatório')
    .min(10, 'Assunto deve ter pelo menos 10 caracteres')
    .max(200, 'Assunto deve ter no máximo 200 caracteres'),
  descricao: z
    .string()
    .min(1, 'Descrição é obrigatória')
    .min(20, 'Descrição deve ter pelo menos 20 caracteres')
    .max(2000, 'Descrição deve ter no máximo 2000 caracteres'),
  prioridade: z
    .number()
    .min(1, 'Prioridade deve estar entre 1 e 4')
    .max(4, 'Prioridade deve estar entre 1 e 4')
    .nullable(),
  categoria: z
    .string()
    .min(1, 'Categoria é obrigatória'),
  nomeContato: z
    .string()
    .min(1, 'Nome do contato é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres'),
  emailContato: z
    .string()
    .min(1, 'Email do contato é obrigatório')
    .email('Email deve ter um formato válido'),
  localidade: z.string().optional()
});

export type ChamadoFormData = z.infer<typeof chamadoSchema>;

// Utilitários de validação
export const validateCPF = (cpf: string): boolean => {
  // Remove caracteres especiais
  const cleanCPF = cpf.replace(/[^\d]/g, '');
  
  // Verifica se tem 11 dígitos
  if (cleanCPF.length !== 11) return false;
  
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
  
  // Validação do algoritmo do CPF
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let digit = 11 - (sum % 11);
  if (digit === 10 || digit === 11) digit = 0;
  if (digit !== parseInt(cleanCPF.charAt(9))) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  digit = 11 - (sum % 11);
  if (digit === 10 || digit === 11) digit = 0;
  if (digit !== parseInt(cleanCPF.charAt(10))) return false;
  
  return true;
};

export const validateCNPJ = (cnpj: string): boolean => {
  const cleanCNPJ = cnpj.replace(/[^\d]/g, '');
  
  if (cleanCNPJ.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;
  
  // Algoritmo de validação do CNPJ
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weights1[i];
  }
  let digit1 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weights2[i];
  }
  let digit2 = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  
  return digit1 === parseInt(cleanCNPJ.charAt(12)) && digit2 === parseInt(cleanCNPJ.charAt(13));
};

// Schema customizado para CPF
export const cpfSchema = z
  .string()
  .min(1, 'CPF é obrigatório')
  .refine(validateCPF, { message: 'CPF inválido' });

// Schema customizado para CNPJ  
export const cnpjSchema = z
  .string()
  .min(1, 'CNPJ é obrigatório')
  .refine(validateCNPJ, { message: 'CNPJ inválido' });

// Schema para validação de senha forte
export const strongPasswordSchema = z
  .string()
  .min(8, 'Senha deve ter pelo menos 8 caracteres')
  .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
  .regex(/[a-z]/, 'Senha deve conter pelo menos uma letra minúscula')
  .regex(/\d/, 'Senha deve conter pelo menos um número')
  .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial');

// Schema para alteração de senha
export const changePasswordSchema = z.object({
  senhaAtual: z.string().min(1, 'Senha atual é obrigatória'),
  novaSenha: strongPasswordSchema,
  confirmaSenha: z.string().min(1, 'Confirmação de senha é obrigatória')
}).refine((data) => data.novaSenha === data.confirmaSenha, {
  message: 'Senhas não conferem',
  path: ['confirmaSenha']
});